import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import {
  Card,
  Skeleton,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { fetchSurveyStats, selectSurveyStats } from '../../../redux/slices/surveyStatsSlice';

const SummaryBarChart = () => {
  const { data, loading } = useSelector(selectSurveyStats);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cardBg = useColorModeValue('white', 'gray.800');
  const labelColor = useColorModeValue('#090c10', '#E2E8F0');

  useEffect(() => {
    if (!data) {
      dispatch(fetchSurveyStats());
    }
  }, [data, dispatch]);

  const chartOptions = useMemo(() => ({
    title: {
      text: 'Overall Survey Summary',
      left: 'center',
      textStyle: { color: labelColor, fontSize: 18 },
    },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '5%', right: '5%', bottom: '5%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['Total', 'Approved', 'Pending', 'Rejected'],
      axisLine: { lineStyle: { color: labelColor } },
      axisLabel: { color: labelColor },
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: labelColor } },
      axisLabel: { color: labelColor },
      splitLine: { lineStyle: { type: 'dashed', color: '#CBD5E0' } },
    },
    series: [{
      name: 'Survey',
      type: 'bar',
      barWidth: '55%',
      data: [
        {
          value: data?.totalCount || 0,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#D8B4FE' },
                { offset: 1, color: '#7C3AED' },
              ],
            },
          },
        },
        {
          value: data?.approved || 0,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#d1fbcf' },
                { offset: 1, color: '#3cdb27' },
              ],
            },
          },
        },
        {
          value: data?.pending || 0,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#BFDBFE' },
                { offset: 1, color: '#1D4ED8' },
              ],
            },
          },
        },
        {
          value: data?.rejected || 0,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#FDE2E2' },
                { offset: 1, color: '#BE123C' },
              ],
            },
          },
        },
      ],
      label: {
        show: true,
        position: 'top',
        color: labelColor,
        fontWeight: 'bold',
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 50,
          shadowColor: 'rgba(0, 0, 0.2, 5)',
        },
      },
    }],
  }), [data, labelColor]);

  const handleClick = (params) => {
    switch (params?.name) {
      case 'Pending':
        navigate('/admin/pendinglist');
        break;
      case 'Approved':
        navigate('/admin/surveylist?status=approved');
        break;
      case 'Rejected':
        navigate('/admin/surveylist?status=rejected');
        break;
      case 'Total':
        navigate('/admin/surveylist');
        break;
      default:
        break;
    }
  };

  return (
    <Card
      w={{ base: '100%', md: '100%', lg: '100%' }}
      mx="auto"
      p={1}
      bg={cardBg}
      shadow="md"
      borderRadius="md"
      mt={4}
    >
      {loading ? (
        <Skeleton height="300px" borderRadius="md" mt={4} />
      ) : (
        <ReactECharts
          option={chartOptions}
          style={{ height: '300px', width: '100%' }}
          onEvents={{ click: handleClick }}
        />
      )}
    </Card>
  );
};

export default SummaryBarChart;
